import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { Alert, Button, Field, Input, Logo } from '../components/ui'
import { ApiError, request } from '../lib/api'
import { useAuth } from '../lib/auth'

function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-block">
            <Logo />
          </Link>
          <h1 className="mt-6 text-2xl font-bold tracking-tight">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{subtitle}</p>}
        </div>
        <div className="card p-6">{children}</div>
        {footer && (
          <p className="mt-6 text-center text-sm text-slate-600 dark:text-slate-400">{footer}</p>
        )}
      </div>
    </div>
  )
}

/** Maps an ApiError onto either a field error or a form-level banner. */
function useFormError() {
  const [error, setError] = useState<ApiError | null>(null)
  return {
    error,
    setError,
    banner: error && !error.field ? error.message : null,
    fieldError: (name: string) =>
      error?.field === name ? (error.details?.message ?? error.message) : undefined,
    reset: () => setError(null),
  }
}

// --------------------------------------------------------------------- login

export function Login() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { banner, setError, reset } = useFormError()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    reset()
    setBusy(true)
    try {
      await login(email, password)
      navigate(params.get('next') ?? '/app', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError('unknown', 'Сталася помилка', 500))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell
      title="Вхід до кабінету"
      subtitle="Керуйте API-ключами, дивіться використання та рахунки"
      footer={
        <>
          Ще немає акаунта?{' '}
          <Link to="/register" className="font-semibold text-brand-600 dark:text-brand-400">
            Зареєструватися
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        {banner && <Alert tone="danger">{banner}</Alert>}

        <Field label="Email" required>
          {(id) => (
            <Input
              id={id}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.ua"
            />
          )}
        </Field>

        <Field label="Пароль" required>
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>

        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-brand-600">
            Забули пароль?
          </Link>
        </div>

        <Button type="submit" loading={busy} className="w-full">
          Увійти
        </Button>
      </form>
    </AuthShell>
  )
}

// ------------------------------------------------------------------ register

export function Register() {
  const { register } = useAuth()
  const navigate = useNavigate()
  const { banner, fieldError, setError, reset } = useFormError()

  const [form, setForm] = useState({
    email: '',
    password: '',
    fullName: '',
    company: '',
    acceptTerms: false,
  })
  const [busy, setBusy] = useState(false)

  const set = (key: keyof typeof form, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    reset()
    setBusy(true)
    try {
      await register(form)
      navigate('/app?welcome=1', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError('unknown', 'Сталася помилка', 500))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell
      title="Створити акаунт"
      subtitle="200 безкоштовних запитів на місяць. Картка не потрібна."
      footer={
        <>
          Вже маєте акаунт?{' '}
          <Link to="/login" className="font-semibold text-brand-600 dark:text-brand-400">
            Увійти
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        {banner && <Alert tone="danger">{banner}</Alert>}

        <Field label="Email" required error={fieldError('email')}>
          {(id) => (
            <Input
              id={id}
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="you@company.ua"
            />
          )}
        </Field>

        <Field
          label="Пароль"
          required
          hint="Мінімум 10 символів, літери та цифри"
          error={fieldError('password')}
        >
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Імʼя">
            {(id) => (
              <Input
                id={id}
                autoComplete="name"
                value={form.fullName}
                onChange={(e) => set('fullName', e.target.value)}
              />
            )}
          </Field>
          <Field label="Компанія">
            {(id) => (
              <Input
                id={id}
                autoComplete="organization"
                value={form.company}
                onChange={(e) => set('company', e.target.value)}
              />
            )}
          </Field>
        </div>

        <label className="flex items-start gap-2.5 text-sm text-slate-600 dark:text-slate-400">
          <input
            type="checkbox"
            required
            checked={form.acceptTerms}
            onChange={(e) => set('acceptTerms', e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          <span>
            Приймаю{' '}
            <Link to="/terms" className="text-brand-600 underline dark:text-brand-400">
              умови використання
            </Link>{' '}
            та{' '}
            <Link to="/privacy" className="text-brand-600 underline dark:text-brand-400">
              політику конфіденційності
            </Link>
          </span>
        </label>

        <Button type="submit" loading={busy} className="w-full">
          Створити акаунт
        </Button>
      </form>
    </AuthShell>
  )
}

// ------------------------------------------------------------ password reset

export function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    // Always reports success — the API never reveals whether an address exists.
    await request('/auth/password-reset', {
      method: 'POST',
      auth: false,
      body: { email },
    }).catch(() => undefined)
    setBusy(false)
    setSent(true)
  }

  return (
    <AuthShell
      title="Відновлення пароля"
      subtitle="Надішлемо посилання для встановлення нового пароля"
      footer={
        <Link to="/login" className="font-semibold text-brand-600 dark:text-brand-400">
          ← Повернутися до входу
        </Link>
      }
    >
      {sent ? (
        <Alert tone="success" title="Перевірте пошту">
          Якщо адреса {email} зареєстрована, лист із посиланням уже надіслано. Посилання
          дійсне 2 години та спрацює один раз.
        </Alert>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email" required>
            {(id) => (
              <Input
                id={id}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.ua"
              />
            )}
          </Field>
          <Button type="submit" loading={busy} className="w-full">
            Надіслати посилання
          </Button>
        </form>
      )}
    </AuthShell>
  )
}

export function ResetPassword() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const { banner, setError, reset } = useFormError()

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    reset()
    setBusy(true)
    try {
      await request('/auth/password-reset/confirm', {
        method: 'POST',
        auth: false,
        body: { token, newPassword: password },
      })
      navigate('/login?reset=1', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError('unknown', 'Сталася помилка', 500))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell title="Новий пароль" subtitle="Після зміни всі активні сесії буде завершено">
      <form onSubmit={submit} className="space-y-4">
        {banner && <Alert tone="danger">{banner}</Alert>}
        {!token && <Alert tone="danger">Посилання неповне — відкрийте його з листа.</Alert>}

        <Field label="Новий пароль" required hint="Мінімум 10 символів, літери та цифри">
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>

        <Button type="submit" loading={busy} disabled={!token} className="w-full">
          Встановити пароль
        </Button>
      </form>
    </AuthShell>
  )
}

export function VerifyEmail() {
  const [params] = useSearchParams()
  const { refreshUser } = useAuth()
  const [state, setState] = useState<'pending' | 'ok' | 'fail'>('pending')
  const token = params.get('token') ?? ''

  useEffect(() => {
    if (!token) {
      setState('fail')
      return
    }
    let cancelled = false
    request(`/auth/verify-email?token=${encodeURIComponent(token)}`, {
      method: 'POST',
      auth: false,
    })
      .then(async () => {
        await refreshUser()
        if (!cancelled) setState('ok')
      })
      .catch(() => {
        if (!cancelled) setState('fail')
      })
    return () => {
      cancelled = true
    }
  }, [token, refreshUser])

  return (
    <AuthShell title="Підтвердження email">
      {state === 'pending' && <p className="text-center text-sm">Перевіряємо посилання…</p>}
      {state === 'ok' && (
        <Alert tone="success" title="Готово">
          Адресу підтверджено. Тепер доступні всі можливості API.
          <Link to="/app" className="btn-primary mt-4 w-full">
            До кабінету
          </Link>
        </Alert>
      )}
      {state === 'fail' && (
        <Alert tone="danger" title="Посилання недійсне">
          Термін дії минув або воно вже використане. Запросіть новий лист у налаштуваннях
          профілю.
        </Alert>
      )}
    </AuthShell>
  )
}
